import { JetView } from "webix-jet";
import { checkedIcon, notCheckedIcon } from "../../helpers/icons";
import { getRolesList } from "../../helpers/roleslist";

export default class RulesView extends JetView {
	config() {
		const _ = this.app.getService("locale")._;
		this.WithRoles = this.app.config.roles;
		this.State = "direct";

		this.Options = [
			{ id: "direct", value: _("Assigned directly") },
			{ id: "all", value: _("All assignments") },
		];

		const form = {
			view: "form",
			rows: [
				{
					rows: [
						{
							view: "template",
							localId: "title",
							borderless: true,
							height: 30,
							css: "webix_um_details_title",
							template: "#name#",
							data: { name: "" },
						},

						{
							cols: [
								{
									view: "radio",
									localId: "radio",
									value: this.State,
									vertical: true,
									options: this.Options,
									on: {
										onChange: o => this.ToggleTable(o),
									},
									hidden: !this.WithRoles,
								},
								{
									view: "template",
									localId: "number",
									height: 38,
									borderless: true,
									css: "webix_um_details_title_description",
									template: v => {
										const number = v.rules ? v.rules.length : 0;
										let descr = !number
											? _("No rules")
											: `${_("Rules assigned")} (${number})`;
										return `<div class="webix_um_details_title_description">${descr}</div>`;
									},
									data: { name: "" },
									hidden: this.WithRoles,
								},
								{
									rows: [
										{
											view: "toggle",
											localId: "toggle",
											offLabel: _("Modify"),
											onLabel: _("Done"),
											width: 160,
											on: {
												onChange: v => this.ToggleVisible(v),
											},
										},
										{},
									],
								},
							],
						},
					],
				},
			],
		};

		const table = {
			view: "datatable",
			localId: "rules",
			rowHeight: webix.skin.$active.rowHeight > 30 ? 60 : 52,
			rowLineHeight: webix.skin.$active.rowHeight > 30 ? 28 : 26,
			css: "webix_um_details_table webix_um_details_table_rules",
			fixedRowHeight: false,
			columns: [
				{
					id: "selected",
					hidden: true,
					header: "",
					width: 45,
					css: "webix_um_checkbox_column",
					cssFormat: (_, obj) => (obj.selected ? "webix_um_row_select" : ""),
					template: obj => {
						return obj.selected ? checkedIcon : notCheckedIcon;
					},
				},
				{
					id: "name",
					header: {
						contentId: "filter",
						content: "textFilter",
						compare: this.GetCompare(),
						prepare: a => a.toLowerCase(),
						placeholder: _("Search"),
					},
					fillspace: 2,
					cssFormat: (_, obj) => (obj.selected ? "webix_um_row_select" : ""),
					template: obj => {
						if (this.State === "direct")
							return (
								obj.short +
								`<div class='webix_um_details_row'>${obj.long}</div>`
							);
						else
							return (
								"<div class='webix_um_assignments_description'>" +
								`<div><span class="webix_um_assignments_rule_name">${obj.short}</span>: ${obj.long}</div>` +
								getRolesList(obj.source) +
								"</div>"
							);
					},
				},
			],
			on: {
				onItemClick: id => this.Toggle(id.row * 1),
				onResize: function() {
					this.adjustRowHeight("name");
				},
			},
			onClick: {
				webix_um_roleslist_item: e => {
					let id = webix.html.locate(e, "data-id");
					this.app.callEvent("details:item-click", ["roles", id]);
				},
			},
		};

		return {
			type: "clean",
			rows: [
				{
					rows: [form, table],
				},
			],
		};
	}

	init() {
		// filters assigned rules for "direct"-state table if "Modify" toggle is off
		this.$$("rules").registerFilter(
			this.$$("toggle"),
			{
				compare(_v1, _v2, obj) {
					return obj.selected;
				},
			},
			{
				getValue: a => (a.getValue() || this.State !== "direct" ? "" : true),
			}
		);
	}

	urlChange() {
		this.ID = this.getParam("id", true);
		this.User = null;
		this.ShowAll = false;
		if (this.State === "direct") this.ShowDirectRules();
		else this.ShowAllAssignments();
	}

	// displays direct or all assigned rules depending on selected state
	ToggleTable(state) {
		this.State = state;
		this.$$("rules").clearAll();
		this.$$("rules")
			.getHeaderContent("filter")
			.setValue("");
		if (state === "direct") {
			this.ShowDirectRules();
			this.$$("toggle").enable();
		} else {
			if (this.ShowAll) this.$$("toggle").setValue(false);
			this.ShowAllAssignments();
			this.$$("toggle").disable();
		}
	}

	SetUser(userItem, roles) {
		if (!this.User) {
			this.User = webix.copy(userItem);
			this.$$("title").setValues(this.User);
			this.User.rules = this.User.rules || [];
			this.User.RulesRoles = this.GetRulesRoles(roles);
		}
	}

	ShowDirectRules() {
		const local = this.app.getService("local");
		Promise.all([local.users(), local.rules(), local.roles()]).then(data => {
			this.SetUser(data[0].getItem(this.ID), data[2]);
			const rules = data[1].serialize().map(({ id, short, long }) => ({
				id,
				short,
				long,
				selected: this.User.rules.indexOf(id) !== -1,
			}));
			this.$$("rules").parse(rules);
			this.ShowNumber();
			this.Filter();
		});
	}

	ShowAllAssignments() {
		const _ = this.app.getService("locale")._;
		const local = this.app.getService("local");
		Promise.all([local.users(), local.rules(), local.roles()]).then(data => {
			this.SetUser(data[0].getItem(this.ID), data[2]);
			const ruleIds = this.GetAllAssignedRules();
			let userRules = [];
			ruleIds.forEach(id => {
				const rule = data[1].getItem(id);
				let source = [];
				// get rules assigned directly to a user (if any)
				if (this.User.rules.indexOf(id) > -1)
					source.push({
						name: _("Assigned directly"),
						direct: true,
					});

				// get rules from assigned roles
				if (this.User.RulesRoles[id]) {
					this.User.RulesRoles[id].forEach(roleId => {
						const role = data[2].getItem(roleId);
						source.push({
							id: role.id,
							name: role.name,
							color: role.color,
						});
					});
				}
				userRules.push({ ...rule, source: source });
			});
			this.$$("rules").parse(userRules);
			this.$$("rules").adjustRowHeight("name");
		});
	}

	Save() {
		this.User.rules = [];
		this.$$("rules").data.each(
			a => {
				if (a.selected) this.User.rules.push(a.id);
			},
			this,
			true
		);
		this.ShowNumber();
		this.app.getService("operations").updateUser(this.User.id, this.User);
	}

	// shows radio labels with the number of direct and all assigned rules
	ShowNumber() {
		if (this.WithRoles) {
			let options = this.$$("radio").config.options;
			options[0].value = `${this.Options[0].value} (${this.User.rules.length})`;
			options[1].value = `${this.Options[1].value} (${
				this.GetAllAssignedRules().length
			})`;
			this.$$("radio").refresh();
		} else this.$$("number").setValues(this.User);
	}

	// returns ids of all assigned rules
	GetAllAssignedRules() {
		let rules = [...this.User.rules];
		const rulesRoles = this.User.RulesRoles;
		for (let id in rulesRoles) {
			if (rules.indexOf(id) < 0) rules.push(id * 1);
		}
		return rules;
	}

	// returns object with a rule id as keys
	// and an array of asssigned roles ids as values
	GetRulesRoles(roles) {
		let rules = {};
		(this.User.roles || []).forEach(id => {
			const role = roles.getItem(id);
			if (role)
				(role.rules || []).forEach(ruleId => {
					if (rules[ruleId]) rules[ruleId].push(id);
					else rules[ruleId] = [id];
				});
		});

		return rules;
	}

	// process "Modify" toggle
	ToggleVisible() {
		this.ShowAll = !this.ShowAll;
		if (this.ShowAll) this.$$("rules").showColumn("selected");
		else this.$$("rules").hideColumn("selected");
		this.Filter();
	}

	// process row click (check) in "Modify" mode
	Toggle(id) {
		if (!this.ShowAll || this.State !== "direct") return;
		const item = this.$$("rules").getItem(id);
		this.$$("rules").updateItem(id, { selected: !item.selected });
		this.Save();
	}

	Filter() {
		this.$$("rules").filterByAll();
		this.$$("rules").sort((a, b) =>
			a.selected == b.selected
				? a.short > b.short
					? 1
					: -1
				: a.selected
				? -1
				: 1
		);
	}

	// shows rows with short and long rule data in filtering results
	GetCompare() {
		return (value, searchValue, rule) => {
			return (
				rule.short.toLowerCase().indexOf(searchValue) > -1 ||
				rule.long.toLowerCase().indexOf(searchValue) > -1
			);
		};
	}
}
